import React from "react";
import { PageHeader } from "../components/pageheader";

/* ---------- Container ---------- */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Section: React.FC<React.PropsWithChildren<{ title: string }>> = ({
  title,
  children,
}) => (
  <section className="mt-10">
    <h2 className="text-xl font-semibold">{title}</h2>
    <div className="mt-3 space-y-3 text-white/70 leading-relaxed">
      {children}
    </div>
  </section>
);

const LAST_UPDATED = "2 September 2026";

/* ---------- Main Component ---------- */
export default function PrivacyPage() {
  return (
    <section className="py-14">
      <Container>
        <PageHeader
          eyebrow="Our Policy"
          title="Privacy Policy"
          text="How BoldBrave AB collects, uses and protects your personal data."
        />

        <div className="mx-auto max-w-3xl">
          <p className="text-sm text-white/50">Last updated: {LAST_UPDATED}</p>

          <Section title="Who we are">
            <p>
              BoldBrave AB (“BoldBrave”, “we”, “us”) is the data controller for
              the personal data described in this policy.
            </p>
            <p>
              BoldBrave AB, C/O Genetor AB, Lokgatan 13G, Malmö, Sweden.
              <br />
              Contact:{" "}
              <a
                className="text-[#FF893F] hover:underline"
                href="mailto:info@bold-n-brave.com"
              >
                info@bold-n-brave.com
              </a>
            </p>
          </Section>

          <Section title="What we collect and why">
            <p>
              <strong className="text-white/90">Contact and enquiries.</strong>{" "}
              If you email us or fill in a form — including lead generation
              forms on LinkedIn — we process the details you provide, typically
              your name, email address, company, job title and the content of
              your message. We use this to answer you and to follow up on a
              possible engagement. Legal basis: our legitimate interest in
              responding to enquiries and doing business, or steps taken at your
              request prior to entering a contract.
            </p>
            <p>
              <strong className="text-white/90">Job applications.</strong> If
              you apply for a role, we process your application and CV to assess
              your candidacy. Legal basis: steps taken at your request prior to
              a possible employment contract, and our legitimate interest in
              recruiting.
            </p>
            <p>
              <strong className="text-white/90">Website usage.</strong> Our site
              is intentionally light. We do not use advertising or tracking
              cookies. Our hosting provider processes standard technical data
              such as IP address and user agent in server logs for security and
              reliability.
            </p>
          </Section>

          <Section title="LinkedIn lead generation forms">
            <p>
              When you submit a LinkedIn lead generation form linked to one of
              our campaigns, LinkedIn shares the fields you confirmed with us
              (for example name, email, company and job title). LinkedIn is a
              separate data controller for the processing that happens on its
              own platform — see the{" "}
              <a
                className="text-[#FF893F] hover:underline"
                href="https://www.linkedin.com/legal/privacy-policy"
                target="_blank"
                rel="noreferrer"
              >
                LinkedIn Privacy Policy
              </a>
              . Once we receive your details, this policy applies. We use them
              to contact you about the offer or content you requested and about
              our related services.
            </p>
          </Section>

          <Section title="Sharing">
            <p>
              We share personal data only with service providers who process it
              on our behalf — such as email, hosting, CRM and analytics-free
              infrastructure providers — and with authorities where required by
              law. We do not sell personal data.
            </p>
            <p>
              Where a provider processes data outside the EU/EEA, we rely on the
              European Commission’s Standard Contractual Clauses or an adequacy
              decision.
            </p>
          </Section>

          <Section title="How long we keep it">
            <p>
              Enquiries and lead data: up to 24 months after our last contact,
              unless a customer relationship is established, in which case we
              keep the data for the duration of the relationship and as required
              by accounting law. Job applications: up to 12 months after the
              recruitment process ends, unless you ask us to keep them longer.
            </p>
          </Section>

          <Section title="Your rights">
            <p>
              Under the GDPR you have the right to access your personal data,
              have it corrected or erased, restrict or object to our processing,
              and receive your data in a portable format. Where we rely on
              legitimate interest, you may object at any time — including to
              direct marketing, which we will then stop.
            </p>
            <p>
              To exercise any of these rights, email{" "}
              <a
                className="text-[#FF893F] hover:underline"
                href="mailto:info@bold-n-brave.com"
              >
                info@bold-n-brave.com
              </a>
              . You also have the right to lodge a complaint with the Swedish
              Authority for Privacy Protection (IMY) or your local supervisory
              authority.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              We may update this policy as our services change. The current
              version is always published on this page with the date above.
            </p>
          </Section>
        </div>
      </Container>
    </section>
  );
}
